/**
 * Interfacce TypeScript allineate ai DTO C# del modulo `Sia.WalletPass`
 * (`Sia.Server.Framework/Core/Sia.WalletPass/DTO/WalletPassRequest.cs` e
 * `WalletPassResult.cs`). Vengono usate per dialogare con gli endpoint
 * `api/wallet-pass-proxy/{google|apple|generate}` esposti dal client
 * `Sia.WalletPass.Client` che proxa al manager remoto.
 */

/** Campo generico key/label/value per le sezioni della pass (secondaryFields). */
export interface WalletPassField {
  key: string;
  label: string;
  value: string;
}

/**
 * Request inviata agli endpoint POST `api/wallet-pass-proxy/{google|apple|generate}`.
 * Tutti i campi `null/undefined` cadono sui default lato server.
 */
export interface WalletPassRequest {
  /** Identificatore univoco della pass (serialNumber sia per Google che per Apple). */
  serialNumber: string;

  /** Payload codificato come QR sulla pass (mirror del valore X_QRCode.repx). */
  qrText: string;

  /** Override del nome organizzazione/issuer; se null cade sui settings. */
  organizationName?: string | null;

  /** Descrizione breve mostrata sulla pass. */
  description?: string | null;

  headerLabel?: string | null;
  headerValue?: string | null;
  primaryLabel?: string | null;
  primaryValue?: string | null;

  secondaryFields?: WalletPassField[];

  /** Override colore di sfondo (hex o rgb()). Null = default settings. */
  backgroundColor?: string | null;
  foregroundColor?: string | null;
  labelColor?: string | null;

  /**
   * Override icona: nome file (es. `"my-icon.png"`) di un PNG gia' presente in
   * `Resources/Shared/Images/` sul manager. Non sono ammessi separatori di path
   * ne `..`. Null o file non trovato => fallback a `icon.png` (warning lato server).
   */
  iconFileName?: string | null;

  /**
   * Override logo: nome file (es. `"my-logo.png"`) di un PNG gia' presente in
   * `Resources/Shared/Images/` sul manager. Non sono ammessi separatori di path
   * ne `..`. Null o file non trovato => fallback a `logo.png` (warning lato server).
   */
  logoFileName?: string | null;

  /** Class id Google Wallet opzionale; null = GoogleWalletSettings.DefaultClassId. */
  googleClassId?: string | null;

  relevantDateUtc?: Date | string | null;
  expirationDateUtc?: Date | string | null;

  /** Codice lingua per i pulsanti localizzati nelle email (it/en/fr/de/es). */
  language?: string | null;
}

/**
 * Result aggregato restituito dagli endpoint di generazione.
 * Ogni campo e' null quando il provider corrispondente e' disabilitato
 * o non e' stato richiesto.
 */
export interface WalletPassResult {
  /** Link "Save to Google Wallet" con JWT firmato; null se non generato. */
  googleSaveLink?: string | null;

  /**
   * URL HTTP magic-link che streamma il .pkpass con MIME
   * application/vnd.apple.pkpass. Da usare nelle email come bottone
   * "Add to Apple Wallet". Null se Apple e' disabilitato, se lo storage
   * .pkpass non e' persistibile o se il ResourceUrl pubblico non e' configurato.
   */
  applePkPassDownloadUrl?: string | null;

  /** Filename suggerito per il .pkpass (es. "{serial}.pkpass"). */
  applePkPassFileName?: string | null;

  /** Snippet HTML email-safe per il bottone "Save to Google Wallet". */
  googleSaveButtonHtml?: string | null;

  /** Snippet HTML email-safe per il bottone "Add to Apple Wallet". */
  applePkPassButtonHtml?: string | null;

  /** Snippet HTML email-safe con entrambi i bottoni affiancati/stacked. */
  walletButtonsHtml?: string | null;
}
